/**
 * Hover-only fabric inspection. The original image, link, and native zoom
 * dialog remain untouched; the transient high-resolution image is decorative.
 *
 * Liquid contract: [data-fd-zoom] on image-only .product-media, with optional
 * data-fd-zoom-width / data-fd-zoom-height containing uploaded source dimensions.
 * Config: [data-fd-shopping-config] JSON with zoomEnabled and zoomMagnification.
 */
(() => {
  if (window.fdHoverZoomInitialized) return;
  window.fdHoverZoomInitialized = true;

  const hoverQuery = matchMedia('(any-hover: hover) and (any-pointer: fine)');
  const selector = '.product-media[data-fd-zoom]';
  let active = null;
  let frame = 0;

  function configuration() {
    try {
      const config = JSON.parse(document.querySelector('[data-fd-shopping-config]')?.textContent || '{}');
      const magnification = Number(config.zoomMagnification);
      return {
        enabled: config.zoomEnabled === true,
        magnification: Number.isFinite(magnification) ? Math.min(4, Math.max(1, magnification)) : 1,
      };
    } catch {
      return { enabled: false, magnification: 1 };
    }
  }

  function stop() {
    if (!active) return;
    const previous = active;
    active = null;
    clearTimeout(previous.timer);
    cancelAnimationFrame(frame);
    frame = 0;
    previous.observer?.disconnect();
    previous.resizeObserver?.disconnect();
    previous.host.classList.remove('fd-zoom-active');
    previous.overlay?.remove();
    if (previous.highImage) {
      previous.highImage.onload = null;
      previous.highImage.onerror = null;
      // Removing src avoids an empty-src request and abandons an unused load.
      previous.highImage.removeAttribute('src');
    }
  }

  function sourceDimensions(host, image) {
    const width = Number(host.dataset.fdZoomWidth);
    const height = Number(host.dataset.fdZoomHeight);
    return {
      width: width > 0 ? width : image.naturalWidth,
      height: height > 0 ? height : image.naturalHeight,
    };
  }

  function renderedDimensions(image, width, height) {
    const boxWidth = image.clientWidth;
    const boxHeight = image.clientHeight;
    const fit = getComputedStyle(image).objectFit;
    if (!(boxWidth > 0 && boxHeight > 0 && width > 0 && height > 0)) return null;

    let scale;
    if (fit === 'cover') scale = Math.max(boxWidth / width, boxHeight / height);
    else if (fit === 'contain') scale = Math.min(boxWidth / width, boxHeight / height);
    else if (fit === 'scale-down') scale = Math.min(1, boxWidth / width, boxHeight / height);
    else if (fit === 'none') scale = 1;
    else return { width: boxWidth, height: boxHeight };
    return { width: width * scale, height: height * scale };
  }

  function imageCandidates(image, sourceWidth) {
    const candidates = [];
    for (const entry of (image.getAttribute('srcset') || '').split(',')) {
      const match = entry.trim().match(/^(\S+)\s+(\d+)w$/);
      if (match) candidates.push({ url: match[1], width: Math.min(Number(match[2]), sourceWidth) });
    }

    const nativeSource = image.getAttribute('data-fd-zoom-src')
      || image.getAttribute('data_max_resolution')
      || image.getAttribute('data-max-resolution');
    if (nativeSource) {
      try {
        const url = new URL(nativeSource, document.baseURI);
        const requestedWidth = Number(url.searchParams.get('width'));
        candidates.push({
          url: url.href,
          width: Math.min(requestedWidth > 0 ? requestedWidth : sourceWidth, sourceWidth),
        });
      } catch {
        // A malformed optional source does not affect normal image rendering.
      }
    }
    return candidates.filter(({ width }) => width > 0).sort((a, b) => a.width - b.width);
  }

  function queuePosition() {
    if (!frame) frame = requestAnimationFrame(updatePosition);
  }

  function updatePosition() {
    frame = 0;
    const session = active;
    if (!session?.overlay) return;
    const { host, image, highImage, overlay } = session;
    if (!host.isConnected || !image.isConnected || image.closest(selector) !== host
      || host.closest('[aria-hidden="true"]')) return stop();

    const imageRect = image.getBoundingClientRect();
    const hostRect = host.getBoundingClientRect();
    if (!(imageRect.width > 0 && imageRect.height > 0 && host.clientWidth > 0 && host.clientHeight > 0)) return stop();
    const x = (session.clientX - imageRect.left) / imageRect.width;
    const y = (session.clientY - imageRect.top) / imageRect.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return stop();

    const source = sourceDimensions(host, image);
    const rendered = renderedDimensions(image, source.width, source.height);
    if (!rendered) return stop();
    // Account for card hover transforms as well as Retina displays. A small
    // uploaded image is never enlarged beyond its available physical pixels.
    const scaleX = hostRect.width / host.clientWidth;
    const scaleY = hostRect.height / host.clientHeight;
    const density = Math.max(1, window.devicePixelRatio || 1);
    const magnification = Math.min(
      session.magnification,
      highImage.naturalWidth / (rendered.width * scaleX * density),
      highImage.naturalHeight / (rendered.height * scaleY * density),
    );
    if (!(magnification > 1.05)) return stop();

    const imageStyle = getComputedStyle(image);
    overlay.style.left = `${(imageRect.left - hostRect.left) / scaleX}px`;
    overlay.style.top = `${(imageRect.top - hostRect.top) / scaleY}px`;
    overlay.style.width = `${imageRect.width / scaleX}px`;
    overlay.style.height = `${imageRect.height / scaleY}px`;
    overlay.style.borderRadius = imageStyle.borderRadius;
    highImage.style.objectFit = imageStyle.objectFit;
    highImage.style.objectPosition = imageStyle.objectPosition;
    highImage.style.transform = `scale(${magnification})`;
    highImage.style.transformOrigin = `${Math.max(0, Math.min(1, x)) * 100}% ${Math.max(0, Math.min(1, y)) * 100}%`;
    host.classList.add('fd-zoom-active');
  }

  function load(session) {
    if (active !== session) return;
    if (!session.host.isConnected || !session.image.complete) return stop();
    const { host, image, magnification } = session;
    const source = sourceDimensions(host, image);
    const rendered = renderedDimensions(image, source.width, source.height);
    if (!rendered) return stop();
    const density = Math.max(1, window.devicePixelRatio || 1);
    const transformScale = image.getBoundingClientRect().width / image.clientWidth;
    const minimumWidth = rendered.width * transformScale * density;
    const requestedWidth = Math.min(source.width, Math.ceil(minimumWidth * magnification));
    const candidates = imageCandidates(image, source.width);
    const candidate = candidates.find(({ width }) => width >= requestedWidth) || candidates.at(-1);
    if (!candidate || candidate.width <= minimumWidth * 1.05) return stop();

    const highImage = new Image();
    session.highImage = highImage;
    highImage.className = 'fd-zoom-image';
    highImage.alt = '';
    highImage.setAttribute('aria-hidden', 'true');
    highImage.draggable = false;
    highImage.decoding = 'async';
    highImage.onload = () => {
      if (active !== session || !host.isConnected || !image.isConnected) return;
      const overlay = document.createElement('span');
      overlay.className = 'fd-zoom-viewport';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.append(highImage);
      session.overlay = overlay;
      host.append(overlay);
      // Observe only while inspecting, so a section/variant replacement removes
      // the stale overlay even when the shopper keeps the pointer stationary.
      session.observer = new MutationObserver(() => {
        if (!host.isConnected || !image.isConnected || !overlay.isConnected
          || host.closest('[aria-hidden="true"]')
          || image.getAttribute('src') !== session.imageSource
          || image.getAttribute('srcset') !== session.imageSources) stop();
      });
      session.observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-hidden', 'src', 'srcset'],
      });
      if (typeof ResizeObserver === 'function') {
        session.resizeObserver = new ResizeObserver(queuePosition);
        session.resizeObserver.observe(host);
        session.resizeObserver.observe(image);
      }
      updatePosition();
    };
    highImage.onerror = () => {
      if (active === session) stop();
    };
    // This is the only point at which an enhanced image is requested.
    highImage.src = candidate.url;
  }

  function inspect(event) {
    if (!hoverQuery.matches || event.pointerType !== 'mouse' || event.buttons !== 0) return stop();
    const host = event.target instanceof Element ? event.target.closest(selector) : null;
    if (!host || host.closest('zoom-dialog, [inert]') || host.closest('[aria-hidden="true"]')) return stop();
    if (active?.host === host) {
      active.clientX = event.clientX;
      active.clientY = event.clientY;
      if (active.overlay) queuePosition();
      return;
    }

    stop();
    const config = configuration();
    if (!config.enabled || config.magnification <= 1) return;
    const image = host.querySelector(':scope > img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return;
    const session = {
      host,
      image,
      imageSource: image.getAttribute('src'),
      imageSources: image.getAttribute('srcset'),
      magnification: config.magnification,
      clientX: event.clientX,
      clientY: event.clientY,
    };
    active = session;
    // A brief intentional-hover delay avoids downloading large images while
    // the shopper simply moves across a product grid.
    session.timer = setTimeout(() => load(session), 100);
  }

  document.addEventListener('pointerover', inspect, { passive: true });
  document.addEventListener('pointermove', inspect, { passive: true });
  document.addEventListener('pointerout', (event) => {
    if (active && !(event.relatedTarget instanceof Node && active.host.contains(event.relatedTarget))) stop();
  }, { passive: true });
  document.addEventListener('pointerdown', stop, { capture: true, passive: true });
  document.addEventListener('pointercancel', stop, { passive: true });
  document.addEventListener('scroll', stop, { capture: true, passive: true });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') stop();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stop();
  });
  document.addEventListener('shopify:section:load', stop);
  window.addEventListener('blur', stop);
  window.addEventListener('resize', stop, { passive: true });
  window.addEventListener('pageshow', stop);
  hoverQuery.addEventListener('change', stop);
})();
